import { z } from 'zod';
import { defineForm } from '../types';
import { ENTRA_USER_PATCHABLE, pickEntraUser } from '../catalogues/entra-user';

/**
 * Change something about an existing user in the client's Entra tenant.
 *
 * The sign-in name says who; every other catalogue attribute is a box that may be left
 * blank, and blank means "leave it as it is". So the validated payload holds exactly the
 * properties to change — the body of the Graph `PATCH /users/{id}` — and nothing else.
 * At least one box must be filled, or there is no request.
 *
 * The selects open on "Choose…" rather than the prefilled answers the create form
 * uses: on an update, a prefilled country would be a change nobody asked for.
 */

const SECTIONS = {
  account: 'Account',
  name: 'Name',
  role: 'Role',
  contact: 'Contact',
  region: 'Region',
} as const;

/** Who to update. Required, and not itself changeable here. */
const account = pickEntraUser(['userPrincipalName'], { section: SECTIONS.account });

const changeOptions = { optional: 'blank', noDefaults: true } as const;
const name = pickEntraUser(['givenName', 'surname'], { ...changeOptions, section: SECTIONS.name });
const role = pickEntraUser(
  [
    'jobTitle',
    'department',
    'companyName',
    'streetAddress',
    'city',
    'state',
    'postalCode',
    'country',
    'employeeId',
  ],
  { ...changeOptions, section: SECTIONS.role },
);
const contact = pickEntraUser(['mobilePhone', 'businessPhones'], {
  ...changeOptions,
  section: SECTIONS.contact,
});
const region = pickEntraUser(['usageLocation', 'preferredLanguage'], {
  ...changeOptions,
  section: SECTIONS.region,
});

export const updateEntraUserSchema = z
  .object({
    ...account.shape,
    ...name.shape,
    ...role.shape,
    ...contact.shape,
    ...region.shape,
  })
  .superRefine((value, ctx) => {
    // An update that changes nothing is a request with nothing in it. Reported on the
    // first box a person would fill, since the renderer shows errors per field.
    const changed = ENTRA_USER_PATCHABLE.some((key) => value[key] !== undefined);
    if (!changed) {
      ctx.addIssue({
        code: 'custom',
        path: ['givenName'],
        message: 'Fill in at least one thing to change',
      });
    }
  });

export type UpdateEntraUserInput = z.input<typeof updateEntraUserSchema>;
export type UpdateEntraUser = z.output<typeof updateEntraUserSchema>;

export const updateEntraUser = defineForm({
  id: 'update-entra-user',
  version: '1.0.0',
  customers: 'all-customers',
  title: 'Update an Entra User',
  description: 'Change details on an existing user. Fill in only what should change.',
  schema: updateEntraUserSchema,
  caseTitle: (payload) => payload.userPrincipalName,
  fields: [
    // The catalogue's username hint is written for creating a user; this one is not,
    // and it searches the directory as the submitter types.
    {
      ...account.fields[0]!,
      widget: 'directory-user',
      helpText: 'The sign-in address of the user to change. Start typing a name to search.',
    },
    ...name.fields,
    ...role.fields,
    ...contact.fields,
    ...region.fields,
  ],
});
